import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import NoteBox from "../../components/noteBox/NoteBox";
import {
  misdeliveryInvestigationInitialValues,
  useMisdeliveryInvestigationForm,
} from "../../@hooks/useMisdeliveryInvestigationForm";
import FormikRadioGroupField from "../../components/shared/form/FormikRadioGroupField";
import { FormikLabeledTextField } from "../../components/shared/form/FormikLabeledTextField";
import FormikSelect from "../../components/shared/form/FormikSelect";
import { SaveOrCompleteMSDInvestigation } from "../../API/saveOrCompleteMSDInvestigation";
import { checkPro } from "../../API/checkPro";
import { getMasterOsdNumberAPI } from "../../API/getMasterOsdNumber";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { useDispatch, useSelector } from "react-redux";
import {
  EFormAction,
  ErrorMessages,
  M_GRID_PROPS,
  S_GRID_PROPS,
  XL_GRID_PROPS,
  YES_NO_OPTIONS,
} from "../../constants";
import Loader from "../../components/loader/loader";
import { IMisdeliveryInvestigation, MisDeliveryStatus } from "../../models";
import { hasCompleteMisdeliveryPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";
import { useCanViewByOwnershipPermission } from "../../@hooks/useCanViewByOwnershipPermission";
import { compareObjects, normalizeProValue } from "../../helpers";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { useImageUploader } from "../../@hooks/useImageUploader";

import Location from "./../overageEntry/Location";
import { useNavigate } from "react-router-dom";
import MisdeliveryInvestigationTemplate from "../../templates/misdeliveryInvestigationTemplate";

interface MisdeliveryInvestigationProps {
  osdNumber: number;
  investigation: IMisdeliveryInvestigation | null | undefined;
  investigationStatus?: MisDeliveryStatus;
  isExpanded?: boolean;
  entryBy?: string;
  assignedTo?: string;
  onRefresh?: () => void | Promise<void>;
  misdeliveredBolPro?: string | number;
  isCleared?: boolean;
}

type InvestigationFormState = IMisdeliveryInvestigation;
type InvestigationPayload = Record<string, unknown>;
type ResetFieldInstruction = { name: string; value: unknown };
type ResetFieldFn = (
  fieldName: string,
  value: unknown,
  currentValues: Record<string, any>,
) => void;
type ResetFieldsFn = (
  fields: ResetFieldInstruction[],
  currentValues: Record<string, any>,
) => void;

const MISDELIVERY_ADDRESS_FIELDS: ResetFieldInstruction[] = [
  { name: "misdeliveryAddress1", value: "" },
  { name: "misdeliveryAddress2", value: "" },
  { name: "misdeliveryCity", value: "" },
  { name: "misdeliveryState", value: "" },
  { name: "misdeliveryZip", value: "" },
];

const TRACKING_IDENTIFICATION_FIELDS: ResetFieldInstruction[] = [
  { name: "doesMsdShipmentHaveLabels", value: "" },
  { name: "isCrossShipment", value: "" },
  { name: "doesFreightHaveSaiaProSticker", value: "" },
  { name: "isProInTransit", value: "" },
  { name: "deliveryStatusOnSaiaPro", value: "" },
  { name: "suffixBillForAppliesToPro", value: undefined },
  { name: "appliedToProShipmentMovingOn", value: undefined },
  { name: "ovExceptionNumber", value: undefined },
  { name: "nbException", value: undefined },
];

const LABEL_AND_ADDRESS_FIELDS: ResetFieldInstruction[] = [
  { name: "isFreightLabeled", value: "" },
  { name: "isFreightLabeledShowingMsdAddress", value: "" },
  ...MISDELIVERY_ADDRESS_FIELDS,
];

const PRO_STICKER_DEPENDENT_FIELDS: ResetFieldInstruction[] = [
  { name: "deliveryStatusOnSaiaPro", value: "" },
  { name: "suffixBillForAppliesToPro", value: undefined },
  { name: "appliedToProShipmentMovingOn", value: undefined },
  { name: "ovExceptionNumber", value: undefined },
];

const NO_LABEL_TRACKING_FIELDS: ResetFieldInstruction[] = [
  { name: "isCrossShipment", value: "" },
  { name: "doesFreightHaveSaiaProSticker", value: "" },
  { name: "deliveryStatusOnSaiaPro", value: "" },
  { name: "suffixBillForAppliesToPro", value: undefined },
  { name: "appliedToProShipmentMovingOn", value: undefined },
  { name: "ovExceptionNumber", value: undefined },
];

const omitFields = (params: InvestigationPayload, fields: string[]) => {
  for (const field of fields) {
    delete params[field];
  }
};

const INVALID_EXCEPTION_ERROR_MESSAGES = {
  asExceptionNumber: "Invalid AS Exception #",
  nbException: "Invalid NB Exception #",
  ovExceptionNumber: "Invalid OV Exception #",
} as const;

type ExceptionFieldName = keyof typeof INVALID_EXCEPTION_ERROR_MESSAGES;

const MASTER_OSD_LOOKUP_PAGE = 1;
const MASTER_OSD_LOOKUP_LIMIT = 10;

const resetPickupCompletionFields = (
  next: InvestigationFormState,
  resetField: ResetFieldFn,
) => {
  if (next.wasPickUpCompleted !== "N") {
    resetField("wasPickUpRescheduled", "", next);
    resetField("pickupNumber", undefined, next);
    resetField("reasonUnableToPickUpCode", "", next);
    resetField("otherReasonComments", "", next);
  }

  if (next.wasPickUpCompleted !== "Y") {
    resetField("wasNewProAttachedToBol", "", next);
    resetField("newProNumber", "", next);
  }
};

const resetPickupRescheduledFields = (
  wasPickUpRescheduled: string | undefined,
  next: InvestigationFormState,
  resetField: ResetFieldFn,
) => {
  if (wasPickUpRescheduled === "Y") {
    resetField("reasonUnableToPickUpCode", "", next);
    resetField("otherReasonComments", "", next);
    return;
  }

  if (wasPickUpRescheduled === "N") {
    resetField("pickupNumber", undefined, next);
    return;
  }

  resetField("pickupNumber", undefined, next);
  resetField("reasonUnableToPickUpCode", "", next);
  resetField("otherReasonComments", "", next);
};

const resetOd400FieldsWhenFreightNotInBay = (
  next: InvestigationFormState,
  resetField: ResetFieldFn,
) => {
  if (next.isFreightInOsdBay === "N") {
    return;
  }

  resetField("didFreightMoveOnOd400", "", next);
  resetField("od400ProNumber", undefined, next);
  resetField("wasOd400CreatedByDock", "", next);
  resetField("misdeliveryPickupProNumber", undefined, next);
  resetField("asExceptionNumber", undefined, next);
};

const resetOd400FieldsByMovement = (
  effectiveDidFreightMoveOnOd400: string | undefined,
  next: InvestigationFormState,
  resetField: ResetFieldFn,
) => {
  if (effectiveDidFreightMoveOnOd400 !== "Y") {
    resetField("od400ProNumber", undefined, next);
    resetField("wasOd400CreatedByDock", "", next);
  }

  if (effectiveDidFreightMoveOnOd400 === "N") {
    resetField("willOriginalProBecomeShort", "", next);
  }

  if (effectiveDidFreightMoveOnOd400 !== "N") {
    resetField("misdeliveryPickupProNumber", undefined, next);
    resetField("asExceptionNumber", undefined, next);
  }
};

const resetHiddenOd400AddressFields = (
  effectiveDidFreightMoveOnOd400: string | undefined,
  next: InvestigationFormState,
  resetField: ResetFieldFn,
  resetFields: ResetFieldsFn,
) => {
  if (effectiveDidFreightMoveOnOd400 !== "N") {
    return;
  }

  resetField("doesAs400AddressMatchBol", "", next);
  resetField("doesBolShowSlc", "", next);
  resetFields(LABEL_AND_ADDRESS_FIELDS, next);
  resetFields(TRACKING_IDENTIFICATION_FIELDS, next);
};

const resetAddressMismatchFields = (
  next: InvestigationFormState,
  resetField: ResetFieldFn,
  resetFields: ResetFieldsFn,
) => {
  if (next.doesAs400AddressMatchBol !== "N") {
    return;
  }

  resetField("doesBolShowSlc", "", next);
  resetFields(LABEL_AND_ADDRESS_FIELDS, next);
  resetFields(TRACKING_IDENTIFICATION_FIELDS, next);
};

const resetFreightLabelFields = (
  next: InvestigationFormState,
  resetField: ResetFieldFn,
  resetFields: ResetFieldsFn,
) => {
  if (next.isFreightLabeled !== "Y") {
    resetField("isFreightLabeledShowingMsdAddress", "", next);
    resetFields(MISDELIVERY_ADDRESS_FIELDS, next);
  }

  if (next.isFreightLabeledShowingMsdAddress !== "N") {
    resetFields(MISDELIVERY_ADDRESS_FIELDS, next);
  }
};

const resetMsdShipmentLabelFields = (
  doesMsdShipmentHaveLabels: string | undefined,
  next: InvestigationFormState,
  resetField: ResetFieldFn,
  resetFields: ResetFieldsFn,
) => {
  if (doesMsdShipmentHaveLabels === "Y") {
    resetField("nbException", undefined, next);
    return;
  }

  if (doesMsdShipmentHaveLabels === "N") {
    resetFields(NO_LABEL_TRACKING_FIELDS, next);
    return;
  }

  resetFields(NO_LABEL_TRACKING_FIELDS, next);
  resetField("nbException", undefined, next);
};

const resetSaiaProStickerFields = (
  next: InvestigationFormState,
  resetField: ResetFieldFn,
  resetFields: ResetFieldsFn,
) => {
  if (String(next.doesFreightHaveSaiaProSticker) !== "Y") {
    resetFields(PRO_STICKER_DEPENDENT_FIELDS, next);
  }

  if (String(next.doesFreightHaveSaiaProSticker) !== "N") {
    resetField("isProInTransit", "", next);
  }
};

const resetDeliveryStatusFields = (
  next: InvestigationFormState,
  resetField: ResetFieldFn,
) => {
  if (next.deliveryStatusOnSaiaPro !== "DS") {
    resetField("suffixBillForAppliesToPro", undefined, next);
  }

  if (next.deliveryStatusOnSaiaPro !== "AS") {
    resetField("appliedToProShipmentMovingOn", undefined, next);
  }

  if (next.deliveryStatusOnSaiaPro !== "DC") {
    resetField("ovExceptionNumber", undefined, next);
  }
};

const getEffectiveDidFreightMoveOnOd400 = (
  next: InvestigationFormState,
) => {
  return next.isFreightInOsdBay === "N" ? next.didFreightMoveOnOd400 : "";
};

const sanitizeOptionalTextValue = (value: unknown) => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value && typeof value === "object") {
    const serialized = JSON.stringify(value);
    return typeof serialized === "string" ? serialized.trim() : "";
  }

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return `${value}`.trim();
  }

  if (typeof value === "symbol") {
    return value.toString().trim();
  }

  return "";
};

const shouldHideActionButtons = (investigationStatus?: MisDeliveryStatus) => {
  return (
    investigationStatus === MisDeliveryStatus.needsCleared ||
    investigationStatus === MisDeliveryStatus.auditInProcess ||
    investigationStatus === MisDeliveryStatus.investigationComplete
  );
};

export const applyPickupRules = (params: InvestigationPayload) => {
  if (params.wasPickUpCompleted !== "N") {
    omitFields(params, [
      "wasPickUpRescheduled",
      "pickupNumber",
      "reasonUnableToPickUpCode",
      "otherReasonComments",
    ]);
  } else if (params.wasPickUpRescheduled !== "Y") {
    omitFields(params, ["pickupNumber"]);
  }

  if (params.wasPickUpRescheduled !== "N") {
    omitFields(params, ["reasonUnableToPickUpCode", "otherReasonComments"]);
  } else if (params.reasonUnableToPickUpCode !== "OR") {
    omitFields(params, ["otherReasonComments"]);
  }

  if (params.wasPickUpCompleted !== "Y") {
    omitFields(params, ["wasNewProAttachedToBol", "newProNumber"]);
  } else if (params.wasNewProAttachedToBol !== "N") {
    omitFields(params, ["newProNumber"]);
  }
};

export const applyOd400Rules = (params: InvestigationPayload) => {
  if (params.isFreightInOsdBay !== "N") {
    omitFields(params, [
      "didFreightMoveOnOd400",
      "od400ProNumber",
      "wasOd400CreatedByDock",
      "misdeliveryPickupProNumber",
      "asExceptionNumber",
    ]);
  } else if (params.didFreightMoveOnOd400 !== "Y") {
    omitFields(params, ["od400ProNumber", "wasOd400CreatedByDock"]);
  } else if (params.wasOd400CreatedByDock !== "Y") {
    omitFields(params, ["od400ProNumber"]);
  }

  // Only include misdeliveryPickupProNumber and asExceptionNumber when didFreightMoveOnOd400 === "N"
  if (params.didFreightMoveOnOd400 !== "N") {
    omitFields(params, ["misdeliveryPickupProNumber", "asExceptionNumber"]);
  }

  if (params.didFreightMoveOnOd400 === "N") {
    omitFields(params, ["willOriginalProBecomeShort"]);
  }
};

export const applyAddressAndLabelRules = (params: InvestigationPayload) => {
  if (params.doesAs400AddressMatchBol === "N") {
    omitFields(params, [
      "doesBolShowSlc",
      "isFreightLabeled",
      "isFreightLabeledShowingMsdAddress",
      "misdeliveryAddress1",
      "misdeliveryAddress2",
      "misdeliveryCity",
      "misdeliveryState",
      "misdeliveryZip",
      "doesMsdShipmentHaveLabels",
      "isCrossShipment",
      "doesFreightHaveSaiaProSticker",
      "isProInTransit",
      "deliveryStatusOnSaiaPro",
      "suffixBillForAppliesToPro",
      "appliedToProShipmentMovingOn",
      "ovExceptionNumber",
      "nbException",
    ]);
  }

  if (params.isFreightLabeled !== "Y") {
    omitFields(params, [
      "isFreightLabeledShowingMsdAddress",
      "misdeliveryAddress1",
      "misdeliveryAddress2",
      "misdeliveryCity",
      "misdeliveryState",
      "misdeliveryZip",
    ]);
  }

  if (params.isFreightLabeledShowingMsdAddress !== "N") {
    omitFields(params, [
      "misdeliveryAddress1",
      "misdeliveryAddress2",
      "misdeliveryCity",
      "misdeliveryState",
      "misdeliveryZip",
    ]);
  }
};

export const applyTrackingRules = (params: InvestigationPayload) => {
  if (params.doesMsdShipmentHaveLabels !== "Y") {
    omitFields(params, [
      "isCrossShipment",
      "doesFreightHaveSaiaProSticker",
      "isProInTransit",
      "deliveryStatusOnSaiaPro",
      "suffixBillForAppliesToPro",
      "appliedToProShipmentMovingOn",
      "ovExceptionNumber",
    ]);
  }

  if (params.doesFreightHaveSaiaProSticker !== "Y") {
    omitFields(params, [
      "deliveryStatusOnSaiaPro",
      "suffixBillForAppliesToPro",
      "appliedToProShipmentMovingOn",
      "ovExceptionNumber",
    ]);
  }

  if (params.doesFreightHaveSaiaProSticker !== "N") {
    omitFields(params, ["isProInTransit"]);
  }

  if (params.doesMsdShipmentHaveLabels !== "N") {
    omitFields(params, ["nbException"]);
  }
};

export const applyDeliveryStatusRules = (params: InvestigationPayload) => {
  if (params.deliveryStatusOnSaiaPro !== "DS") {
    omitFields(params, ["suffixBillForAppliesToPro"]);
  }

  if (params.deliveryStatusOnSaiaPro !== "AS") {
    omitFields(params, ["appliedToProShipmentMovingOn"]);
  }

  if (params.deliveryStatusOnSaiaPro !== "DC") {
    omitFields(params, ["ovExceptionNumber"]);
  }
};

const MisdeliveryInvestigation: React.FC<MisdeliveryInvestigationProps> = ({
  osdNumber,
  investigation,
  investigationStatus,
  isExpanded,
  entryBy,
  assignedTo,
  onRefresh,
  misdeliveredBolPro,
  isCleared,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uploadImage } = useImageUploader();
  const { formik } = useMisdeliveryInvestigationForm(
    investigation ?? undefined,
  );
  const { values } = formik;
  const typedValues = values as InvestigationFormState;
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastCreatedValuesRef = useRef<InvestigationFormState | null>(null);
  const hasCompleteMisdeliveryPermission = useSelector(
    hasCompleteMisdeliveryPermissionSelector,
  );

  const canViewCompleteButton = useCanViewByOwnershipPermission({
    enabledFor: hasCompleteMisdeliveryPermission,
    entryBy,
    assignedTo,
  });

  const bolGenerated = investigationStatus === MisDeliveryStatus.bolGenerated;

  const INVALID_PRO_MSG = "Invalid OD400#";

  // Tooltip mapping for reasonUnableToPickUpCode field
  const REASON_UNABLE_TO_PICKUP_TOOLTIPS: Record<string, { text: string; key: string }> = {
    CC: { text: "If the consignee corrected the misdelivery", key: "reasonUnableToPickUpCode-consignee-corrected" },
    CK: { text: "If the consignee kept/inventoried the freight", key: "reasonUnableToPickUpCode-consignee-keeping-freight" },
    SC: { text: "If the shipper corrected", key: "reasonUnableToPickUpCode-shipper-corrected" },
    OR: { text: "Reach out to OSDCLP@saia.com", key: "reasonUnableToPickUpCode-other-reasons" },
  };

  const od400ProValidationSeqRef = useRef(0);
  const od400ProValidationCacheRef = useRef<{
    value: string;
    isValid: boolean;
  } | null>(null);

  const hasAttemptedCompleteRef = useRef(false);
  const invalidServerExceptionValueRef = useRef<
    Partial<Record<ExceptionFieldName, string>>
  >({});

  const setInvalidServerExceptionValue = useCallback(
    (fieldName: ExceptionFieldName, value: string | undefined) => {
      if (value) {
        invalidServerExceptionValueRef.current[fieldName] = value;
        return;
      }

      delete invalidServerExceptionValueRef.current[fieldName];
    },
    [],
  );

  const getReasonUnableToPickUpTooltip = () => {
    const code = String(typedValues.reasonUnableToPickUpCode ?? "");
    return REASON_UNABLE_TO_PICKUP_TOOLTIPS[code];
  };

  const getCurrentFormValues = (): InvestigationFormState =>
    formik.values as InvestigationFormState;

  const isOd400Applicable = (nextValues: InvestigationFormState) =>
    nextValues.isFreightInOsdBay === "N" &&
    nextValues.didFreightMoveOnOd400 === "Y";

  const applyOd400CachedErrorIfNeeded = (nextValues: InvestigationFormState) => {
    if (!isOd400Applicable(nextValues)) return;

    const current = (nextValues.od400ProNumber ?? "").toString().trim();
    if (!current) return;

    if (!current.startsWith("555")) {
      formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
      return;
    }

    const cached = od400ProValidationCacheRef.current;
    if (cached?.value === current && cached?.isValid === false) {
      formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
    }
  };

  const validateOd400ProNumber = useCallback(
    async (rawValue: string): Promise<boolean> => {
      const value = rawValue.trim();
      if (!value) return true;

      // Rule 1: must start with 555
      if (!value.startsWith("555")) {
        od400ProValidationCacheRef.current = { value, isValid: false };
        formik.setFieldTouched("od400ProNumber", true, false);
        formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
        return false;
      }

      const seq = ++od400ProValidationSeqRef.current;
      try {
        const response = await checkPro(value);
        // Ignore stale responses if the user changed the field quickly.
        if (seq !== od400ProValidationSeqRef.current) return true;

        const isBlankObject =
          response &&
          typeof response === "object" &&
          Object.keys(response as Record<string, unknown>).length === 0;

        const isValid = Boolean(response && !isBlankObject);
        od400ProValidationCacheRef.current = { value, isValid };
        formik.setFieldTouched("od400ProNumber", true, false);

        if (isValid) {
          const formErrors = formik.errors as Record<string, unknown>;
          if (formErrors?.od400ProNumber === INVALID_PRO_MSG) {
            formik.setFieldError("od400ProNumber", undefined);
          }
        } else {
          formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
        }

        return isValid;
      } catch {
        if (seq !== od400ProValidationSeqRef.current) return true;
        od400ProValidationCacheRef.current = { value, isValid: false };
        formik.setFieldTouched("od400ProNumber", true, false);
        formik.setFieldError("od400ProNumber", INVALID_PRO_MSG);
        return false;
      }
    },
    [formik],
  );

  const validateExceptionNumber = useCallback(
    async (
      fieldName: ExceptionFieldName,
      osdType: "AS" | "NB" | "OV",
      rawValue: unknown,
    ): Promise<boolean> => {
      const value = String(rawValue ?? "").trim();
      if (!value) {
        setInvalidServerExceptionValue(fieldName, undefined);
        return true;
      }

      const params = {
        search: value.toString(),
        page: MASTER_OSD_LOOKUP_PAGE,
        limit: MASTER_OSD_LOOKUP_LIMIT,
        osdType: [osdType].join(","),
        visibility: "nonCleared",
      };

      const invalidMessage = INVALID_EXCEPTION_ERROR_MESSAGES[fieldName];

      try {
        const results = await getMasterOsdNumberAPI(params as any);
        const options = Array.isArray(results)
          ? results
          : Array.isArray((results as any)?.data)
            ? (results as any).data
            : [];

        const normalizedValue = value.toLowerCase();
        const hasExactMatch = options.some(
          (option: unknown) =>
            String(option ?? "").trim().toLowerCase() === normalizedValue,
        );

        formik.setFieldTouched(fieldName, true, false);

        if (!hasExactMatch) {
          setInvalidServerExceptionValue(fieldName, value);
          formik.setFieldError(fieldName, invalidMessage);
          return false;
        }

        setInvalidServerExceptionValue(fieldName, undefined);
        const currentFieldError = (formik.errors as Record<string, unknown>)[fieldName];
        if (currentFieldError === invalidMessage) {
          formik.setFieldError(fieldName, undefined);
        }

        return true;
      } catch {
        setInvalidServerExceptionValue(fieldName, value);
        formik.setFieldTouched(fieldName, true, false);
        formik.setFieldError(fieldName, invalidMessage);
        return false;
      }
    },
    [formik, setInvalidServerExceptionValue],
  );

  const prevValuesRef = useRef<InvestigationFormState>(
    values as InvestigationFormState,
  );

  // Helper: Reset field value, touched state, and error
  const resetField = useCallback(
    (
      fieldName: string,
      value: unknown,
      currentValues: Record<string, any>,
    ) => {
      if (currentValues[fieldName] !== value) {
        formik.setFieldValue(fieldName, value, false);
      }
      formik.setFieldTouched(fieldName, false, false);
      formik.setFieldError(fieldName, undefined);
    },
    [formik],
  );

  const resetFields = useCallback(
    (
      fields: ResetFieldInstruction[],
      currentValues: Record<string, any>,
    ) => {
      fields.forEach(({ name, value }) => resetField(name, value, currentValues));
    },
    [resetField],
  );

  // Helper: Handle pickup flow field changes
  const handlePickupFlowChanges = useCallback(
    (prev: InvestigationFormState, next: InvestigationFormState) => {
      if (prev.wasPickUpCompleted !== next.wasPickUpCompleted) {
        resetPickupCompletionFields(next, resetField);
      }

      if (
        prev.wasNewProAttachedToBol !== next.wasNewProAttachedToBol &&
        next.wasNewProAttachedToBol !== "N"
      ) {
        resetField("newProNumber", "", next);
      }

      if (prev.wasPickUpRescheduled !== next.wasPickUpRescheduled) {
        resetPickupRescheduledFields(next.wasPickUpRescheduled, next, resetField);
      }

      if (
        prev.reasonUnableToPickUpCode !== next.reasonUnableToPickUpCode &&
        next.reasonUnableToPickUpCode !== "OR"
      ) {
        resetField("otherReasonComments", "", next);
      }
    },
    [resetField],
  );

  // Helper: Handle OSD bay and OD400 flow changes
  const handleOsdBayFlowChanges = useCallback(
    (prev: InvestigationFormState, next: InvestigationFormState) => {
      const effectiveDidFreightMoveOnOd400 =
        getEffectiveDidFreightMoveOnOd400(next);

      if (prev.isFreightInOsdBay !== next.isFreightInOsdBay) {
        resetOd400FieldsWhenFreightNotInBay(next, resetField);
      }

      if (prev.didFreightMoveOnOd400 !== next.didFreightMoveOnOd400) {
        resetOd400FieldsByMovement(
          effectiveDidFreightMoveOnOd400,
          next,
          resetField,
        );
      }

      resetHiddenOd400AddressFields(
        effectiveDidFreightMoveOnOd400,
        next,
        resetField,
        resetFields,
      );

      if (
        prev.wasOd400CreatedByDock !== next.wasOd400CreatedByDock &&
        next.wasOd400CreatedByDock !== "Y"
      ) {
        resetField("od400ProNumber", undefined, next);
      }
    },
    [resetField, resetFields],
  );

  // Helper: Handle BOL and address field changes
  const handleBolAndAddressChanges = useCallback(
    (prev: InvestigationFormState, next: InvestigationFormState) => {
      if (
        prev.doesAs400AddressMatchBol !== next.doesAs400AddressMatchBol &&
        next.doesAs400AddressMatchBol === "N"
      ) {
        resetAddressMismatchFields(next, resetField, resetFields);
      }

      if (prev.isFreightLabeled !== next.isFreightLabeled) {
        resetFreightLabelFields(next, resetField, resetFields);
      }

      if (
        prev.isFreightLabeledShowingMsdAddress !==
          next.isFreightLabeledShowingMsdAddress &&
        next.isFreightLabeledShowingMsdAddress !== "N"
      ) {
        resetFields(MISDELIVERY_ADDRESS_FIELDS, next);
      }

      if (prev.doesMsdShipmentHaveLabels !== next.doesMsdShipmentHaveLabels) {
        resetMsdShipmentLabelFields(
          next.doesMsdShipmentHaveLabels,
          next,
          resetField,
          resetFields,
        );
      }

      if (
        prev.doesFreightHaveSaiaProSticker !==
        next.doesFreightHaveSaiaProSticker
      ) {
        resetSaiaProStickerFields(next, resetField, resetFields);
      }

      if (prev.deliveryStatusOnSaiaPro !== next.deliveryStatusOnSaiaPro) {
        resetDeliveryStatusFields(next, resetField);
      }
    },
    [resetField, resetFields],
  );

  // Keep form state aligned with current UI visibility, including initial API data.
  const normalizeHiddenFieldsByUiRules = useCallback(
    (next: InvestigationFormState) => {
      const effectiveDidFreightMoveOnOd400 =
        getEffectiveDidFreightMoveOnOd400(next);

      resetPickupCompletionFields(next, resetField);

      if (next.wasPickUpRescheduled !== "Y") {
        resetField("pickupNumber", undefined, next);
      }

      if (next.wasPickUpRescheduled !== "N") {
        resetField("reasonUnableToPickUpCode", "", next);
        resetField("otherReasonComments", "", next);
      }

      if (next.reasonUnableToPickUpCode !== "OR") {
        resetField("otherReasonComments", "", next);
      }

      if (next.wasNewProAttachedToBol !== "N") {
        resetField("newProNumber", "", next);
      }

      resetOd400FieldsWhenFreightNotInBay(next, resetField);
      resetOd400FieldsByMovement(
        effectiveDidFreightMoveOnOd400,
        next,
        resetField,
      );

      if (next.wasOd400CreatedByDock !== "Y") {
        resetField("od400ProNumber", undefined, next);
      }

      resetHiddenOd400AddressFields(
        effectiveDidFreightMoveOnOd400,
        next,
        resetField,
        resetFields,
      );
      resetAddressMismatchFields(next, resetField, resetFields);
      resetFreightLabelFields(next, resetField, resetFields);

      if (next.doesMsdShipmentHaveLabels !== "Y") {
        resetFields(NO_LABEL_TRACKING_FIELDS, next);
      }

      if (next.doesMsdShipmentHaveLabels !== "N") {
        resetField("nbException", undefined, next);
      }

      if (next.doesFreightHaveSaiaProSticker !== "Y") {
        resetFields(PRO_STICKER_DEPENDENT_FIELDS, next);
      }

      if (next.doesFreightHaveSaiaProSticker !== "N") {
        resetField("isProInTransit", "", next);
      }

      resetDeliveryStatusFields(next, resetField);
    },
    [resetField, resetFields],
  );

  useEffect(() => {
    const prev = prevValuesRef.current;
    const next = values as InvestigationFormState;

    handlePickupFlowChanges(prev, next);
    handleOsdBayFlowChanges(prev, next);
    applyOd400CachedErrorIfNeeded(next);
    handleBolAndAddressChanges(prev, next);
    normalizeHiddenFieldsByUiRules(next);

    prevValuesRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    const formValues = formik.values as InvestigationFormState;
    const validationVisibilityByField: Record<ExceptionFieldName, boolean> = {
      asExceptionNumber:
        formValues.isFreightInOsdBay === "N" &&
        formValues.didFreightMoveOnOd400 === "N",
      nbException: formValues.doesMsdShipmentHaveLabels === "N",
      ovExceptionNumber: formValues.deliveryStatusOnSaiaPro === "DC",
    };

    (Object.keys(INVALID_EXCEPTION_ERROR_MESSAGES) as ExceptionFieldName[]).forEach(
      (fieldName) => {
        const trackedInvalidValue =
          invalidServerExceptionValueRef.current[fieldName];
        if (!trackedInvalidValue) {
          return;
        }

        const isVisible = validationVisibilityByField[fieldName];
        const currentValue = String(formValues[fieldName] ?? "").trim();

        if (!isVisible || !currentValue || currentValue !== trackedInvalidValue) {
          setInvalidServerExceptionValue(fieldName, undefined);
          return;
        }

        const invalidMessage = INVALID_EXCEPTION_ERROR_MESSAGES[fieldName];
        const fieldErrors = formik.errors as Record<string, unknown>;
        const fieldTouched = formik.touched as Record<string, unknown>;

        if (fieldErrors[fieldName] !== invalidMessage) {
          formik.setFieldError(fieldName, invalidMessage);
        }

        if (!fieldTouched[fieldName]) {
          formik.setFieldTouched(fieldName, true, false);
        }
      },
    );
  }, [formik, setInvalidServerExceptionValue]);

  const touchAllFields = useCallback(() => {
    const allTouched = Object.keys(formik.values).reduce(
      (acc, key) => {
        acc[key] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    formik.setTouched(allTouched, false);
    return allTouched;
  }, [formik]);

  const sanitizeYesNoField = (
    params: InvestigationPayload,
    fieldName: string,
  ) => {
    const value = params[fieldName];
    if (value !== "Y" && value !== "N") {
      delete params[fieldName];
    }
  };

  const YES_NO_FIELDS = [
    "wasPickUpCompleted",
    "wasPickUpRescheduled",
    "wasNewProAttachedToBol",
    "isFreightInOsdBay",
    "didFreightMoveOnOd400",
    "wasOd400CreatedByDock",
    "willOriginalProBecomeShort",
    "doesAs400AddressMatchBol",
    "doesBolShowSlc",
    "isFreightLabeled",
    "isFreightLabeledShowingMsdAddress",
    "doesMsdShipmentHaveLabels",
    "isCrossShipment",
    "doesFreightHaveSaiaProSticker",
    "isProInTransit",
  ] as const;

  const sanitizeNumberField = (
    params: InvestigationPayload,
    fieldName: string,
  ) => {
    const value = params[fieldName];

    if (value === "" || value === null || value === undefined) {
      delete params[fieldName];
      return;
    }

    if (typeof value === "number") {
      return;
    }

    const coerced = Number(value);
    if (Number.isFinite(coerced)) {
      params[fieldName] = coerced;
    } else {
      delete params[fieldName];
    }
  };

  const sanitizeOptionalTextField = (
    params: InvestigationPayload,
    fieldName: string,
  ) => {
    const value = params[fieldName];

    if (value === undefined || value === null) {
      delete params[fieldName];
      return;
    }

    const text = sanitizeOptionalTextValue(value);
    if (!text) {
      delete params[fieldName];
      return;
    }

    params[fieldName] = text;
  };

  const sanitizeInvestigationPayload = (rawValues: InvestigationPayload) => {
    const allowedKeys = Object.keys(misdeliveryInvestigationInitialValues);
    const params = allowedKeys.reduce((acc, key) => {
      if (Object.hasOwn(rawValues, key)) {
        acc[key] = rawValues[key];
      }
      return acc;
    }, {} as InvestigationPayload);

    // Radio fields: backend validates strictly as Y/N. On Save, we allow partial forms,
    // so omit any unset values ("", undefined) to avoid 400 validation errors.
    for (const fieldName of YES_NO_FIELDS) {
      sanitizeYesNoField(params, fieldName);
    }

    // Drop dependent fields when not applicable (prevents stale values in "Save" payload).
    applyPickupRules(params);
    applyOd400Rules(params);
    applyAddressAndLabelRules(params);
    applyTrackingRules(params);
    applyDeliveryStatusRules(params);

    // Keep optional freight handling names whenever provided.
    sanitizeOptionalTextField(params, "driverName");
    sanitizeOptionalTextField(params, "dockWorkerName");

    // Numeric fields: keep only finite numbers in payload (never send string values).
    const numericFields = [
      "od400ProNumber",
      "pickupNumber",
      "newProNumber",
      "misdeliveryPickupProNumber",
      "asExceptionNumber",
      "appliedToProShipmentMovingOn",
      "ovExceptionNumber",
      "nbException",
    ] as const;

    for (const fieldName of numericFields) {
      sanitizeNumberField(params, fieldName);
    }

    return params;
  };

  const applyZeroForMissingExistingNumericFields = (
    params: InvestigationPayload,
    investigation: IMisdeliveryInvestigation | null | undefined,
  ) => {
    const hasOwn = (fieldName: string) =>
      Object.hasOwn(params, fieldName);

    const hasExistingNumericValue = (fieldName: keyof IMisdeliveryInvestigation) => {
      const value = investigation?.[fieldName];
      return typeof value === "number" && Number.isFinite(value);
    };

    const setZeroWhenCleared = (
      fieldName: keyof IMisdeliveryInvestigation,
      shouldApply: boolean,
    ) => {
      if (shouldApply && hasExistingNumericValue(fieldName) && !hasOwn(fieldName)) {
        params[fieldName] = 0;
      }
    };

    setZeroWhenCleared("od400ProNumber", params.didFreightMoveOnOd400 === "Y");
    setZeroWhenCleared("pickupNumber", params.wasPickUpRescheduled === "Y");
    setZeroWhenCleared("newProNumber", params.wasNewProAttachedToBol === "N");
    setZeroWhenCleared(
      "misdeliveryPickupProNumber",
      params.didFreightMoveOnOd400 === "N",
    );
    setZeroWhenCleared("asExceptionNumber", params.didFreightMoveOnOd400 === "N");
    setZeroWhenCleared(
      "appliedToProShipmentMovingOn",
      params.deliveryStatusOnSaiaPro === "AS",
    );
    setZeroWhenCleared("ovExceptionNumber", params.deliveryStatusOnSaiaPro === "DC");
    setZeroWhenCleared(
      "nbException",
      params.doesMsdShipmentHaveLabels === "N",
    );
  };

  // Helper: Focus field by name
  const focusFieldByName = useCallback((name: string) => {
    document?.getElementsByName(name)?.[0]?.focus();
  }, []);

  // Helper: Validate OD400 if applicable
  const validateOd400IfNeeded = useCallback(async (): Promise<boolean> => {
    if (!isOd400Applicable(formik.values as InvestigationFormState)) return true;

    const od400Value = String(
      (formik.values as InvestigationFormState).od400ProNumber ?? "",
    ).trim();
    if (!od400Value) return true;

    const isValid = await validateOd400ProNumber(od400Value);
    if (!isValid) {
      focusFieldByName("od400ProNumber");
    }
    return isValid;
  }, [formik.values, focusFieldByName, validateOd400ProNumber]);

  const validateExceptionNumbersIfNeeded = useCallback(async (): Promise<boolean> => {
    const formValues = formik.values as InvestigationFormState;
    const checks: Array<{
      shouldValidate: boolean;
      fieldName: "asExceptionNumber" | "nbException" | "ovExceptionNumber";
      osdType: "AS" | "NB" | "OV";
      value: unknown;
    }> = [
      {
        shouldValidate:
          formValues.isFreightInOsdBay === "N" &&
          formValues.didFreightMoveOnOd400 === "N",
        fieldName: "asExceptionNumber",
        osdType: "AS",
        value: formValues.asExceptionNumber,
      },
      {
        shouldValidate: formValues.doesMsdShipmentHaveLabels === "N",
        fieldName: "nbException",
        osdType: "NB",
        value: formValues.nbException,
      },
      {
        shouldValidate: formValues.deliveryStatusOnSaiaPro === "DC",
        fieldName: "ovExceptionNumber",
        osdType: "OV",
        value: formValues.ovExceptionNumber,
      },
    ];

    const checksRequiringApi = checks.filter((check) => {
      if (!check.shouldValidate) {
        return false;
      }

      return String(check.value ?? "").trim().length > 0;
    });

    if (!checksRequiringApi.length) {
      return true;
    }

    setLoading(true);

    try {
      for (const check of checksRequiringApi) {
        const isValid = await validateExceptionNumber(
          check.fieldName,
          check.osdType,
          check.value,
        );

        if (!isValid) {
          focusFieldByName(check.fieldName);
          return false;
        }
      }

      return true;
    } finally {
      setLoading(false);
    }
  }, [focusFieldByName, formik.values, validateExceptionNumber]);

  // Helper: Validate all fields for complete action
  const validateAllFields = useCallback(async (): Promise<boolean> => {
    const errors = await formik.validateForm();
    formik.setErrors(errors);

    if (Object.keys(errors).length > 0) {
      focusFieldByName(Object.keys(errors)?.[0]);
      touchAllFields();
      return false;
    }
    return true;
  }, [formik, focusFieldByName, touchAllFields]);

  const buildSaveParams = () => {
    const params = sanitizeInvestigationPayload(
      getCurrentFormValues() as InvestigationPayload,
    );
    applyZeroForMissingExistingNumericFields(params, investigation);
    return params;
  };

  const handleSuccessfulSave = (
    successMessage: string,
    resetCompleteAttempt: boolean = true,
  ) => {
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.SUCCESS,
        successMessage,
        Date.now().toString(),
      ),
    );

    setIsEditing(false);
    lastCreatedValuesRef.current = { ...getCurrentFormValues() };
    dispatch(isFormValueChanged(false));
    if (resetCompleteAttempt) {
      hasAttemptedCompleteRef.current = false;
    }
  };

  const handleSaveError = (error: unknown, fallbackMessage: string) => {
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof error === "string" ? error : fallbackMessage,
        Date.now().toString(),
      ),
    );
  };

  const runPostSaveRefresh = async (continueOnError: boolean) => {
    if (!onRefresh) return;

    try {
      await onRefresh();
    } catch (refreshError) {
      if (!continueOnError) {
        throw refreshError;
      }
    }
  };

  const executeSaveAction = async (
    action: "save" | "complete",
    options?: {
      successMessage?: string;
      errorMessage?: string;
      navigateTo?: string;
      continueOnRefreshError?: boolean;
      skipPostSaveRefresh?: boolean;
    },
  ) => {
    setLoading(true);
    try {
      const params = buildSaveParams();
      await SaveOrCompleteMSDInvestigation(params, osdNumber, action);

      handleSuccessfulSave(
        options?.successMessage ??
          (action === EFormAction.complete
            ? ErrorMessages.ohInvestigationNoteCompleted
            : ErrorMessages.investigationNoteSaved),
      );

      if (!options?.skipPostSaveRefresh) {
        await runPostSaveRefresh(options?.continueOnRefreshError ?? false);
      }

      if (options?.navigateTo) {
        navigate(options.navigateTo);
      }
    } catch (error) {
      handleSaveError(
        error,
        options?.errorMessage ?? `OS&D investigation notes ${action} failed`,
      );
    } finally {
      setLoading(false);
    }
  };

  const getImageProNumber = useCallback(() => {
    return normalizeProValue(misdeliveredBolPro);
  }, [misdeliveredBolPro]);

  const saveOrComplete = async (action: "save" | "complete" = "save") => {
    const isComplete = action === "complete";

    if (isComplete) {
      hasAttemptedCompleteRef.current = true;
    }

    // Validate OD400 when applicable
    const isOd400Valid = await validateOd400IfNeeded();
    if (!isOd400Valid) return;

    // Validate AS/NB/OV exception numbers when they are present.
    const areExceptionNumbersValid = await validateExceptionNumbersIfNeeded();
    if (!areExceptionNumbersValid) return;

    // Validate all fields if completing
    if (isComplete) {
      const allFieldsValid = await validateAllFields();
      if (!allFieldsValid) return;
    }

    await executeSaveAction(action, {
      continueOnRefreshError: true,
      skipPostSaveRefresh: isComplete,
    });

    if (isComplete) {
      const imageProNumber = getImageProNumber();
      if (imageProNumber === undefined) {
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.INFO,
            "Misdelivery BOL/PRO is not present, so skipping the investigation image upload.",
            Date.now().toString(),
          ),
        );
        await runPostSaveRefresh(true);
        return;
      }

      try {
        await uploadImage({
          elementId: "misdelivery-investigation-image-temp",
          osdNumber,
          setLoading,
          proNumber: imageProNumber,
          headers: {
            "OSD-Module-Name": "misdelivery",
            "OSD-Section-Name": "investigation",
            "OSD-Action-Type": "upload",
            "OSD-Exception-Type": "MSD",
          },
        });
      } catch (uploadError) {
        const message =
          uploadError instanceof Error && uploadError.message
            ? uploadError.message
            : "Image upload failed";

        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            message,
            Date.now().toString(),
          ),
        );
      } finally {
        await runPostSaveRefresh(true);
      }
    }
  };

  const saveAndNavigateToCreateFlow = async (targetPath: string) => {
    // Keep Save semantics (partial allowed), then route to the respective create flow.
    const isOd400Valid = await validateOd400IfNeeded();
    if (!isOd400Valid) return;

    const areExceptionNumbersValid = await validateExceptionNumbersIfNeeded();
    if (!areExceptionNumbersValid) return;

    await executeSaveAction("save", {
      navigateTo: targetPath,
      continueOnRefreshError: true,
      errorMessage: "OS&D investigation notes save failed",
    });
  };

  const handleSaveAndCreateNB = async () => {
    await saveAndNavigateToCreateFlow(
      `/overage/entry?osdType=NB&misdeliveryOsdNumber=${encodeURIComponent(String(osdNumber))}`,
    );
  };

  const handleSaveAndCreateOV = async () => {
    await saveAndNavigateToCreateFlow(
      `/overage/entry?osdType=OV&misdeliveryOsdNumber=${encodeURIComponent(String(osdNumber))}`,
    );
  };

  // Handler for "Save and Create AS" when didFreightMoveOnOd400 === "N"
  const handleSaveAndCreateAS = async () => {
    await saveAndNavigateToCreateFlow(
      `/all-shortages/entry?misdeliveryOsdNumber=${encodeURIComponent(String(osdNumber))}`,
    );
  };

  // ─── Style Constants ────────────────────────────────────────────────────────
  const buttonStyles = {
    clearCancel: { marginRight: "8px", color: "white", borderRadius: "12px" },
    save: { marginRight: "8px", backgroundColor: "#2196f3", color: "white" },
    saveAndCreateAS: {
      marginRight: "8px",
      backgroundColor: "#4caf50",
      color: "white",
    },
    complete: {
      marginRight: "8px",
      backgroundColor: "#4caf50",
      color: "white",
    },
  };

  // Check if freight did NOT move on OD400 (AC2.4 flow)
  const isFreightDidNotMoveOnOd400 =
    values.isFreightInOsdBay === "N" && values.didFreightMoveOnOd400 === "N";
  const hasAsExceptionValue =
    String(typedValues.asExceptionNumber ?? "").trim().length > 0;

  // NB flow: when MSD shipment has no labels.
  const isNoLabelsNBFlow = values.doesMsdShipmentHaveLabels === "N";
  const hasNbExceptionValue =
    String(typedValues.nbException ?? "").trim().length > 0;
  const isDeliveryStatusDC = values.deliveryStatusOnSaiaPro === "DC";
  const hasOvExceptionValue =
    String(typedValues.ovExceptionNumber ?? "").trim().length > 0;
  const shouldUseSaveFlowForAs = isFreightDidNotMoveOnOd400 && hasAsExceptionValue;
  const shouldUseSaveFlowForNb = isNoLabelsNBFlow && hasNbExceptionValue;
  const shouldUseSaveFlowForOv = isDeliveryStatusDC && hasOvExceptionValue;

  const getFieldError = (fieldName: string): string | undefined => {
    const touched = formik.touched as Record<string, unknown>;
    const errors = formik.errors as Record<string, unknown>;
    const fieldError = errors[fieldName];

    if (touched[fieldName] && typeof fieldError === "string") {
      return fieldError;
    }

    return undefined;
  };

  const renderCompleteButton = (shouldShow: boolean) => {
    if (!shouldShow) return null;

    return (
      <Button
        type="button"
        variant="contained"
        className="entry"
        onClick={() => saveOrComplete("complete")}
        style={buttonStyles.complete}
      >
        Complete & Image
      </Button>
    );
  };

  const renderSaveButton = () => (
    <Button
      type="button"
      variant="contained"
      className="entry"
      onClick={() => saveOrComplete()}
      style={buttonStyles.save}
    >
      Save
    </Button>
  );

  const renderStandardEditingButtons = () => {
    return (
      <>
        {renderSaveButton()}
        {renderCompleteButton(canViewCompleteButton)}
      </>
    );
  };

  const renderCreateExceptionButton = (
    onClick: () => void,
    label: string,
  ) => {
    return (
      <>
        <Button
          type="button"
          variant="contained"
          className="entry"
          onClick={onClick}
          style={buttonStyles.saveAndCreateAS}
        >
          {label}
        </Button>
        {renderCompleteButton(false)}
      </>
    );
  };

  const renderEditingPrimaryActionButtons = () => {
    if (shouldUseSaveFlowForAs || shouldUseSaveFlowForNb || shouldUseSaveFlowForOv) {
      return renderStandardEditingButtons();
    }

    if (isFreightDidNotMoveOnOd400) {
      return renderCreateExceptionButton(
        handleSaveAndCreateAS,
        "Save & Create AS",
      );
    }

    if (isNoLabelsNBFlow) {
      return renderCreateExceptionButton(
        handleSaveAndCreateNB,
        "Save & Create NB",
      );
    }

    if (isDeliveryStatusDC) {
      return renderCreateExceptionButton(
        handleSaveAndCreateOV,
        "Save & Create OV",
      );
    }

    return renderStandardEditingButtons();
  };

  // ─── Action Buttons Renderer ─────────────────────────────────────────────────
  const renderActionButtons = () => {
    if (shouldHideActionButtons(investigationStatus)) {
      return;
    }

    if (isEditing) {
      const clearOrCancelLabel = bolGenerated ? "Clear" : "Cancel";

      return (
        <>
          <Button
            type="button"
            variant="outlined"
            className="clear-btn"
            onClick={() => {
              formik.resetForm();
              dispatch(isFormValueChanged(false));
              hasAttemptedCompleteRef.current = false;
            }}
            style={buttonStyles.clearCancel}
          >
            {clearOrCancelLabel}
          </Button>
          {renderEditingPrimaryActionButtons()}
        </>
      );
    }

    return (
      <Button
        type="button"
        variant="contained"
        className="assign mr-0 ml"
        onClick={() => {
          lastCreatedValuesRef.current = { ...formik.values };
          dispatch(isFormValueChanged(false));
          setIsEditing(true);
        }}
      >
        Edit
      </Button>
    );
  };

  useEffect(() => {
    if (!isEditing) {
      dispatch(isFormValueChanged(false));
      return;
    }

    const lastCreatedValues = lastCreatedValuesRef.current;
    if (!lastCreatedValues) return;

    const formData = formik.values as InvestigationFormState;
    const hasChanged = !compareObjects(formData, lastCreatedValues);
    dispatch(isFormValueChanged(hasChanged));
  }, [dispatch, formik.values, isEditing]);

  useEffect(() => {
    if (isExpanded) {
      setIsEditing(bolGenerated);
      formik.resetForm();
      lastCreatedValuesRef.current = {
        ...(formik.initialValues as InvestigationFormState),
      };
      dispatch(isFormValueChanged(false));
      hasAttemptedCompleteRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  useEffect(() => {
    // Whenever the investigation data changes (refetch / response update),
    // keep the form in view mode and sync current values to the new initial values.
    formik.resetForm();
    lastCreatedValuesRef.current = {
      ...(formik.initialValues as InvestigationFormState),
    };
    dispatch(isFormValueChanged(false));
    hasAttemptedCompleteRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigation]);

  useEffect(() => {
    setIsEditing(bolGenerated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationStatus]);

  return (
    <>
      <Box className="misdelivery-ivestigation-container">
        <form onSubmit={formik.handleSubmit} noValidate>
          <h3 className="accordion-content-header">Pickup Recovery</h3>

          <Grid
            container
            spacing={2}
            className="filter-margin"
            id="investigation-form"
          >
            <FormikRadioGroupField
              formik={formik}
              name="wasPickUpCompleted"
              label="Was the misdelivered freight pick up completed?"
              title="Was the misdelivered freight pick up completed?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            {values.wasPickUpCompleted === "Y" && (
              <>
                <FormikRadioGroupField
                  formik={formik}
                  name="wasNewProAttachedToBol"
                  label="If the new PRO was attached to the BOL ?"
                  required
                  options={YES_NO_OPTIONS}
                  disabled={!isEditing}
                  gridProps={S_GRID_PROPS}
                />

                {values.wasNewProAttachedToBol === "N" && (
                  <FormikLabeledTextField
                    formik={formik}
                    name="newProNumber"
                    label="New PRO #"
                    placeholder="Enter New PRO Number"
                    required
                    type="number"
                    maxLength={11}
                    disabled={!isEditing}
                    gridProps={{ md: 3.2, xs: 12 }}
                  />
                )}
              </>
            )}

            {values.wasPickUpCompleted === "N" && (
              <FormikRadioGroupField
                formik={formik}
                name="wasPickUpRescheduled"
                label="Was pick up rescheduled ?"
                required
                options={YES_NO_OPTIONS}
                disabled={!isEditing}
                gridProps={S_GRID_PROPS}
              />
            )}

            {values.wasPickUpRescheduled === "Y" && (
              <FormikLabeledTextField
                formik={formik}
                name="pickupNumber"
                label="New PU #"
                placeholder="Enter PU Number"
                required
                maxLength={7}
                type="number"
                disabled={!isEditing}
                gridProps={{ md: 3.2, xs: 12 }}
              />
            )}
            {values.wasPickUpRescheduled === "N" && (
              <FormikSelect
                name="reasonUnableToPickUpCode"
                label="Reason pickup cannot be completed"
                value={String(typedValues.reasonUnableToPickUpCode ?? "")}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={"Please select Reason"}
                options={[
                  { value: "CC", label: "Consignee Corrected" },
                  { value: "CK", label: "Consignee is keeping or has inventoried the freight" },
                  { value: "SC", label: "Shipper Corrected" },
                  { value: "OR", label: "Other Reason" },
                ]}
                tooltip={getReasonUnableToPickUpTooltip()}
                error={getFieldError("reasonUnableToPickUpCode")}
                required
                disabled={!isEditing}
                gridProps={{ md: 3, xs: 12 }}
              />
            )}

            {values.wasPickUpRescheduled === "N" &&
              values.reasonUnableToPickUpCode === "OR" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="otherReasonComments"
                  label="Additional comments"
                  placeholder="Enter additional comments"
                  required
                  maxLength={200}
                  disabled={!isEditing}
                  gridProps={{ md: 3, xs: 12 }}
                  multiline={true}
                  rows={2}
                />
              )}

            <Grid item {...XL_GRID_PROPS}>
              <NoteBox message="Note: All written statements should have the pro(s) in reference to be valid" />
              {(values.wasPickUpCompleted === "Y" ||
                values.wasPickUpCompleted === "N") && (
                <>
                  {values.reasonUnableToPickUpCode === "CC" && (
                    <>
                      <Box mt={1}>
                        <NoteBox message="Note: Correct consignee signed a delivery receipt." />
                      </Box>
                      <Box mt={1}>
                        <NoteBox message="Note: Correct consignee provided a written statement." />
                      </Box>
                      <Box mt={1}>
                        <NoteBox message="Note: Shipper/3PL provided a written statement that the consignee received." />
                      </Box>
                    </>
                  )}
                  {values.reasonUnableToPickUpCode === "CK" && (
                    <Box mt={1}>
                      <NoteBox message="Note: Confirm written statement from the shipper/3pl stating, they are offsetting or invoicing customers(s) for the freight they kept and/or that no claim will be filed on the apply to pro that will be left short." />
                    </Box>
                  )}
                  {values.reasonUnableToPickUpCode === "SC" && (
                    <>
                      <Box mt={1}>
                        <NoteBox message="Note: Confirm the correcting carrier tracking #." />
                      </Box>
                      <Box mt={1}>
                        <NoteBox message="Note: Valid written statement from the shipper/3PL stating they corrected." />
                      </Box>
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>

          <h3 className="accordion-content-header">
            Freight Location Verification
          </h3>

          <Grid container spacing={2} className="filter-margin">
            <FormikRadioGroupField
              formik={formik}
              name="isFreightInOsdBay"
              label="Is the Misdelivered freight in the OS&D Bay ?"
              required
              options={YES_NO_OPTIONS}
              disabled={!isEditing}
              gridProps={S_GRID_PROPS}
            />

            {values.isFreightInOsdBay === "N" && (
              <FormikRadioGroupField
                formik={formik}
                name="didFreightMoveOnOd400"
                label="Did the freight move on an OD400"
                required
                options={YES_NO_OPTIONS}
                disabled={!isEditing}
                gridProps={S_GRID_PROPS}
              />
            )}

            {values.isFreightInOsdBay === "N" &&
              values.didFreightMoveOnOd400 === "Y" && (
                <FormikRadioGroupField
                  formik={formik}
                  name="wasOd400CreatedByDock"
                  label="Was an OD400 created by the dock ?"
                  required
                  options={YES_NO_OPTIONS}
                  disabled={!isEditing}
                  gridProps={S_GRID_PROPS}
                />
              )}

            {values.isFreightInOsdBay === "N" &&
              values.didFreightMoveOnOd400 === "Y" &&
              values.wasOd400CreatedByDock === "Y" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="od400ProNumber"
                  label="OD PRO#"
                  placeholder="Enter OD PRO#"
                  required
                  type="number"
                  maxLength={11}
                  disabled={!isEditing}
                />
              )}

            {values.isFreightInOsdBay === "N" &&
              values.didFreightMoveOnOd400 === "N" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="misdeliveryPickupProNumber"
                  label="All short the MISDELV pickup PRO#"
                  placeholder="Enter MISDELV pickup PRO#"
                  required
                  type="number"
                  maxLength={11}
                  disabled={!isEditing}
                />
              )}

            {values.isFreightInOsdBay === "N" &&
              values.didFreightMoveOnOd400 === "N" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="asExceptionNumber"
                  label="AS Exception #"
                  placeholder="Enter AS Exception #"
                  required
                  type="number"
                  maxLength={9}
                  disabled={!isEditing}

                  // tooltip={{
                  //   text: "MSD should not move on OD400, if you cannot find pro the freight applies to have this entered as an overage.",
                  //   key: "od400ProNumber",
                  // }}
                />
              )}
          </Grid>
          <Box mt={1}>
            <NoteBox message="Note: If the MISDELV freight was lost or unaccounted for after the recovery, and the pick terminal is currently unable to locate the freight on the dock - All short the MISDELV PICK UP PRO and begin shortage investigation." />
          </Box>
          <br />

          {values.didFreightMoveOnOd400 !== "N" && (
            <>
              <Grid container spacing={2} className="filter-margin">
                <FormikRadioGroupField
                  formik={formik}
                  name="willOriginalProBecomeShort"
                  label="When the misdelivered freight is picked up, will the original misdelivered PRO become short ?"
                  required
                  options={YES_NO_OPTIONS}
                  disabled={!isEditing}
                  gridProps={XL_GRID_PROPS}
                />
              </Grid>

              {values.willOriginalProBecomeShort === "Y" && (
                <Box mt={1}>
                  <NoteBox
                    message={
                      <>
                        Note: Update delivery status on the 86 screen, Enter
                        Correct piece count. Enter shortage description.{" "}
                        <b>Do NOT enter an ADE</b>. Use <b>Shift + F8</b> to
                        unfold delivery status.
                      </>
                    }
                  />
                  <br />
                </Box>
              )}

              <Grid
                container
                spacing={2}
                paddingLeft={2}
                marginBottom={0}
                className="filter-margin"
              >
                <h3 className="accordion-content-header">
                  Address & Billing Verification
                </h3>
              </Grid>
              {/* AS400 BOL address match control */}
              <Grid container spacing={2} className="filter-margin">
                <FormikRadioGroupField
                  formik={formik}
                  name="doesAs400AddressMatchBol"
                  label="Does the address in AS400 match the BOL?"
                  required
                  options={YES_NO_OPTIONS}
                  disabled={!isEditing}
                  gridProps={S_GRID_PROPS}
                  tooltipText="Once the F2 correction has been made, move shipment out on original PRO"
                  tooltipKey="doesAs400AddressMatchBol"
                  tooltipUsePortal
                />

                {values.doesAs400AddressMatchBol !== "N" && (
                  <>
                    <FormikRadioGroupField
                      formik={formik}
                      name="doesBolShowSlc"
                      label="Does the BOL show SLC ?"
                      required
                      options={YES_NO_OPTIONS}
                      disabled={!isEditing}
                      gridProps={S_GRID_PROPS}
                    />

                    {/* New row with MSD address control */}

                    {/* New field: Is the freight labeled? */}
                    <FormikRadioGroupField
                      formik={formik}
                      name="isFreightLabeled"
                      label="Does the shipment have any label?"
                      required
                      options={YES_NO_OPTIONS}
                      disabled={!isEditing}
                      gridProps={S_GRID_PROPS}
                    />

                    {values.isFreightLabeled === "Y" && (
                      <Grid item {...S_GRID_PROPS}>
                        <FormikRadioGroupField
                          formik={formik}
                          name="isFreightLabeledShowingMsdAddress"
                          label="Does the freight label show the misdelivery pick-up address?"
                          title="Does the freight label show the misdelivery pick-up address?"
                          required
                          options={YES_NO_OPTIONS}
                          disabled={!isEditing}
                          gridProps={XL_GRID_PROPS}
                        />

                        {values.isFreightLabeledShowingMsdAddress === "Y" && (
                          <Grid
                            item
                            {...XL_GRID_PROPS}
                            className="mt-10"
                            data-testid="billing-error-note"
                          >
                            <NoteBox message="Note: Shipper error - New BOL required." />
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </>
                )}

                {values.doesAs400AddressMatchBol === "N" && (
                  <Grid
                    container
                    spacing={2}
                    paddingLeft={2}
                    className="filter-margin"
                  >
                    <Grid
                      item
                      {...XL_GRID_PROPS}
                      className="mt-10"
                      data-testid="billing-error-note"
                    >
                      <NoteBox message="Note: Submit the billing corrections (f2). Once the F2 correction has been completed, move the shipment out using the original PRO." />
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {values.isFreightLabeledShowingMsdAddress === "N" && (
                <Grid
                  container
                  spacing={2}
                  paddingLeft={2}
                  marginBottom={0}
                  className="filter-margin"
                >
                  <h3 className="accordion-content-header">
                    Freight Label Address
                  </h3>
                </Grid>
              )}

              {/* Fifth row with BOL SLC control */}
              <Grid container spacing={2} className="filter-margin">
                {values.isFreightLabeledShowingMsdAddress === "N" && (
                  <>
                    <FormikLabeledTextField
                      formik={formik}
                      name="misdeliveryAddress1"
                      label="Address 1"
                      required={true}
                      placeholder="Enter address"
                      maxLength={30}
                      disabled={!isEditing}
                      gridProps={S_GRID_PROPS}
                    />

                    <FormikLabeledTextField
                      formik={formik}
                      name="misdeliveryAddress2"
                      label="Address 2"
                      placeholder="Enter address"
                      maxLength={30}
                      disabled={!isEditing}
                      gridProps={S_GRID_PROPS}
                    />

                    <Location
                      osdDetails={investigation}
                      formFieldsDisabled={!isEditing}
                      formik={formik}
                      key={String(formik.values.misdeliveryState ?? "")}
                      formObject={{
                        cityField: "misdeliveryCity",
                        zipField: "misdeliveryZip",
                        stateField: "misdeliveryState",
                        cityLabel: "City",
                        zipLabel: "Zip",
                        stateLabel: "State",
                      }}
                      required={true}
                    />
                  </>
                )}

                {values.doesAs400AddressMatchBol !== "N" && (
                  <>
                    <Grid
                      container
                      spacing={2}
                      paddingTop={2}
                      marginLeft={2}
                      marginBottom={0}
                      className="filter-margin"
                    >
                      <h3
                        style={{ marginBottom: 0 }}
                        className="accordion-content-header"
                      >
                        MISDELV Shipment Tracking Identification
                      </h3>
                    </Grid>

                    <FormikRadioGroupField
                      formik={formik}
                      name="doesMsdShipmentHaveLabels"
                      label="Does the misdelivered shipment have any labels?"
                      required
                      options={YES_NO_OPTIONS}
                      disabled={!isEditing}
                      gridProps={{ md: 3.1, xs: 12 }}
                    />

                    {values.doesMsdShipmentHaveLabels === "Y" && (
                      <>
                        <FormikRadioGroupField
                          formik={formik}
                          name="isCrossShipment"
                          label="Is this a cross shipment?"
                          required
                          options={YES_NO_OPTIONS}
                          disabled={!isEditing}
                          gridProps={S_GRID_PROPS}
                        />
                        <FormikRadioGroupField
                          formik={formik}
                          name="doesFreightHaveSaiaProSticker"
                          label="Does the freight have a SAIA PRO sticker?"
                          required
                          tooltipText="If the PRO# on the freight applies to a shipment that has clear delivery. Hold the freight to confirm the delivery status with the consignee."
                          tooltipUsePortal
                          options={YES_NO_OPTIONS}
                          disabled={!isEditing}
                          gridProps={{ md: 2.9, xs: 12 }}
                        />
                        {values.doesFreightHaveSaiaProSticker === "N" && (
                          <FormikRadioGroupField
                            formik={formik}
                            name="isProInTransit"
                            label="Is the PRO in transit ?"
                            required
                            tooltipText="Contact Terminal."
                            tooltipUsePortal
                            options={YES_NO_OPTIONS}
                            disabled={!isEditing}
                            gridProps={S_GRID_PROPS}
                          />
                        )}
                        {values.doesFreightHaveSaiaProSticker === "Y" && (
                          <FormikSelect
                            name="deliveryStatusOnSaiaPro"
                            label="Verify the delivery status on the SAIA PRO # ?"
                            value={
                              String(typedValues.deliveryStatusOnSaiaPro ?? "")
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            options={[
                              { value: "DS", label: "Delivered Short (DS)" },
                              { value: "AS", label: "All Short (AS)" },
                              { value: "DC", label: "Delivered Clear (DC)" },
                            ]}
                            error={getFieldError("deliveryStatusOnSaiaPro")}
                            required
                            disabled={!isEditing}
                            gridProps={S_GRID_PROPS}
                          />
                        )}
                      </>
                    )}

                    {values.doesMsdShipmentHaveLabels === "N" && (
                      <FormikLabeledTextField
                        formik={formik}
                        name="nbException"
                        label="NB Exception #"
                        placeholder="Enter NB Exception #"
                        required
                        type="number"
                        tooltip={{
                          text: "Create the NB/OV and complete the overage investigation.",
                          key: "nbException",
                          usePortal: true,
                        }}
                        maxLength={9}
                        disabled={!isEditing}
                        gridProps={S_GRID_PROPS}
                      />
                    )}
                  </>
                )}
              </Grid>
              <Grid container spacing={2} className="filter-margin">
                <Grid item {...{ md: 3.1, xs: 12 }}>
                  
                </Grid>
                <Grid item {...S_GRID_PROPS}>
                 {values.doesMsdShipmentHaveLabels === "Y" && (
                    <NoteBox message="Note: Do not send the freight on OD400. Hold the freight until the misdelivery has been picked up from the cross shipment." />
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2} className="filter-margin">
                <Grid item {...{ md: 3.1, xs: 12 }}>
                </Grid>
                <Grid item {...S_GRID_PROPS}>
                  {values.isCrossShipment === "Y" && (
                    <NoteBox message="Note: CONTACT DESTINATION TRM" />
                  )}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item {...S_GRID_PROPS}>
                  {values.deliveryStatusOnSaiaPro === "DS" && (
                    <>
                      <FormikLabeledTextField
                        formik={formik}
                        name="suffixBillForAppliesToPro"
                        label="List suffix bill for the applies to PRO#"
                        placeholder="Enter suffix bill"
                        required
                        maxLength={20}
                        disabled={!isEditing}
                        gridProps={XL_GRID_PROPS}
                      />
                      <br />
                      <NoteBox message="Note: Apply the shortage on the suffix." />
                    </>
                  )}

                  {values.deliveryStatusOnSaiaPro === "AS" && (
                    <>
                      <FormikLabeledTextField
                        formik={formik}
                        name="appliedToProShipmentMovingOn"
                        label="List the applied to PRO # shipment is moving on"
                        placeholder="Enter applied to PRO#"
                        required
                        type="number"
                        maxLength={11}
                        disabled={!isEditing}
                        gridProps={XL_GRID_PROPS}
                      />
                      <br />
                      <NoteBox message="Note: Attached shipment to the AS PRO." />
                    </>
                  )}

                  {values.deliveryStatusOnSaiaPro === "DC" && (
                    <>
                      <FormikLabeledTextField
                        formik={formik}
                        name="ovExceptionNumber"
                        label="OV Exception #"
                        placeholder="Enter OV Exception #"
                        required
                        type="number"
                        maxLength={9}
                        disabled={!isEditing}
                        gridProps={XL_GRID_PROPS}
                      />
                      <br />
                      <NoteBox message="Note: Create OV for shipment." />
                    </>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          <h3 className="accordion-content-header">Freight Handling</h3>

          {/* New row with driver name text input */}
          <Grid container spacing={2} className="filter-margin">
            <FormikLabeledTextField
              formik={formik}
              name="driverName"
              label="Driver: who delivered the shipment"
              placeholder="Enter driver name"
              maxLength={30}
              disabled={!isEditing}
              gridProps={M_GRID_PROPS}
            />

            <FormikLabeledTextField
              formik={formik}
              name="dockWorkerName"
              label="Dockworker: who loaded the shipment onto P&D trailer"
              title="Dockworker: who loaded the shipment onto P&D trailer"
              placeholder="Enter dockworker name"
              maxLength={30}
              disabled={!isEditing}
              gridProps={M_GRID_PROPS}
            />
          </Grid>

          <Grid
            container
            spacing={2}
            className="filter-margin"
            justifyContent="flex-end"
          >
            {investigationStatus !== MisDeliveryStatus.hazmatPending &&
              !isCleared && <Grid item>{renderActionButtons()}</Grid>}
          </Grid>
        </form>
      </Box>
      <div
        id="misdelivery-investigation-image-temp"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: "600px",
          background: "#fff",
          padding: "24px",
          color: "#000",
          boxSizing: "border-box",
        }}
      >
        <MisdeliveryInvestigationTemplate
          osdNumber={osdNumber}
          values={typedValues}
        />
      </div>
      <Loader pageLoader={loading}></Loader>
    </>
  );
};

export default MisdeliveryInvestigation;